import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { relativeTime } from '@/utils/relativeTime';
import {
   listExtraAddressesApi,
   addExtraAddressApi,
   removeExtraAddressApi,
   type ExtraAddressRow,
} from '@/api/vendo';

/**
 * "Antenna / radio IPs" (Ray 2026-10-04, WiFi5-Soft's "Access Point Static").
 * Point-to-point radios (LiteBeam, airFiber) keep a factory IP like
 * 192.168.10.20; the router adds an address in that network so the operator
 * can open the radio's settings page from the vendo Wi-Fi.
 */

interface Props {
   deviceId: string;
   /** Sub-vendo VLANs on this router, for the Interface dropdown. */
   vlans: { vlanId: number; label: string }[];
}

const ifaceLabel = (iface: string, vlans: Props['vlans']) => {
   if (iface === 'lan') return 'Main LAN';
   const id = Number(iface.replace(/^vlan/, ''));
   const v = vlans.find((z) => z.vlanId === id);
   return v?.label ? `VLAN ${id} · ${v.label}` : `VLAN ${id}`;
};

const errorOf = (err: any, fallback: string) => err?.response?.data?.error || fallback;

const AntennaIpsSection = ({ deviceId, vlans }: Props) => {
   const queryClient = useQueryClient();
   const key = ['extra-addresses', deviceId];
   const { data, isLoading } = useQuery({
      queryKey: key,
      queryFn: () => listExtraAddressesApi(deviceId),
   });
   const rows: ExtraAddressRow[] = data?.extraAddresses ?? [];

   const [adding, setAdding] = useState(false);
   const [iface, setIface] = useState('lan');
   const [address, setAddress] = useState('');
   const [mask, setMask] = useState('255.255.255.0');
   const [formError, setFormError] = useState('');
   const [busy, setBusy] = useState(false);
   const [confirmId, setConfirmId] = useState<string | null>(null);

   const openAdd = () => {
      setIface('lan');
      setAddress('');
      setMask('255.255.255.0');
      setFormError('');
      setAdding(true);
   };

   const save = async () => {
      if (busy) return;
      if (!address.trim()) {
         setFormError('Type the IP for the router, e.g. 192.168.10.1');
         return;
      }
      setBusy(true);
      setFormError('');
      try {
         const r = await addExtraAddressApi(deviceId, { iface, address: address.trim(), mask: mask.trim() });
         queryClient.setQueryData(key, { extraAddresses: r.extraAddresses });
         if (r.warning) toast.info(r.warning, { autoClose: 8000 });
         else toast.success(`Added ${address.trim()} — open your radio from the vendo Wi-Fi in about a minute`, { autoClose: 3500 });
         setAdding(false);
      } catch (err) {
         setFormError(errorOf(err, 'Could not save. Try again.'));
      } finally {
         setBusy(false);
      }
   };

   const remove = async (row: ExtraAddressRow) => {
      if (busy) return;
      setBusy(true);
      try {
         const r = await removeExtraAddressApi(deviceId, row._id);
         queryClient.setQueryData(key, { extraAddresses: r.extraAddresses });
         toast.success(`Removed ${row.address}`, { autoClose: 2500 });
         setConfirmId(null);
      } catch (err) {
         toast.error(errorOf(err, 'Could not remove. Try again.'));
      } finally {
         setBusy(false);
      }
   };

   return (
      <Section>
         <SectionTitle>Antenna / radio IPs</SectionTitle>
         <Hint>
            Add an IP in your radio&apos;s network so you can open its settings. Example: radio at
            192.168.10.20 → add 192.168.10.1, mask 255.255.255.0. Open it from a paid or Unlimited
            phone on the vendo Wi-Fi.
         </Hint>

         {isLoading ? (
            <Hint>Loading…</Hint>
         ) : rows.length === 0 ? (
            <Hint>No radio IPs yet.</Hint>
         ) : (
            <Table role="table" aria-label="Antenna / radio IPs">
               <HeadRow role="row">
                  <span>Interface</span>
                  <span>Address</span>
                  <span>Mask</span>
                  <span>Added</span>
                  <span />
               </HeadRow>
               {rows.map((r) => (
                  <Row role="row" key={r._id}>
                     <span>{ifaceLabel(r.iface, vlans)}</span>
                     <Mono>{r.address}</Mono>
                     <Mono>{r.mask}</Mono>
                     <span title={new Date(r.addedAt).toLocaleString()}>{relativeTime(r.addedAt)}</span>
                     <RemoveBtn type="button" onClick={() => setConfirmId(r._id)}>
                        Remove
                     </RemoveBtn>
                  </Row>
               ))}
            </Table>
         )}

         <div>
            <AddBtn type="button" onClick={openAdd} disabled={rows.length >= 10}>
               + Add radio IP
            </AddBtn>
         </div>

         {adding &&
            createPortal(
               // Never closes on an outside click — only Cancel / Save (Ray's rule).
               <Overlay>
                  <Dialog role="dialog" aria-label="Add radio IP">
                     <DialogTitle>Add radio IP</DialogTitle>
                     <Field>
                        <Label htmlFor="xa-iface">Interface</Label>
                        <Select id="xa-iface" value={iface} onChange={(e) => setIface(e.target.value)}>
                           <option value="lan">Main LAN</option>
                           {vlans.map((v) => (
                              <option key={v.vlanId} value={`vlan${v.vlanId}`}>
                                 {ifaceLabel(`vlan${v.vlanId}`, vlans)}
                              </option>
                           ))}
                        </Select>
                     </Field>
                     <Field>
                        <Label htmlFor="xa-address">Address</Label>
                        <Input
                           id="xa-address"
                           inputMode="decimal"
                           placeholder="e.g. 192.168.10.1"
                           value={address}
                           onChange={(e) => setAddress(e.target.value.replace(/[^0-9.]/g, ''))}
                        />
                     </Field>
                     <Field>
                        <Label htmlFor="xa-mask">Mask</Label>
                        <Input
                           id="xa-mask"
                           inputMode="decimal"
                           placeholder="e.g. 255.255.255.0"
                           value={mask}
                           onChange={(e) => setMask(e.target.value.replace(/[^0-9./]/g, ''))}
                        />
                     </Field>
                     {formError && <ErrorText role="alert">{formError}</ErrorText>}
                     <Actions>
                        <GhostBtn type="button" onClick={() => setAdding(false)} disabled={busy}>
                           Cancel
                        </GhostBtn>
                        <PrimaryBtn type="button" onClick={save} disabled={busy}>
                           {busy ? 'Saving…' : 'Save'}
                        </PrimaryBtn>
                     </Actions>
                  </Dialog>
               </Overlay>,
               document.body
            )}

         {confirmId &&
            createPortal(
               <Overlay>
                  <Dialog role="dialog" aria-label="Remove radio IP">
                     {(() => {
                        const row = rows.find((r) => r._id === confirmId);
                        return (
                           <>
                              <DialogTitle>Remove {row?.address}?</DialogTitle>
                              <Hint>You won&apos;t be able to open that radio from the vendo Wi-Fi any more.</Hint>
                              <Actions>
                                 <GhostBtn type="button" onClick={() => setConfirmId(null)} disabled={busy}>
                                    Keep
                                 </GhostBtn>
                                 <DangerBtn type="button" onClick={() => row && remove(row)} disabled={busy}>
                                    {busy ? 'Removing…' : 'Yes, remove'}
                                 </DangerBtn>
                              </Actions>
                           </>
                        );
                     })()}
                  </Dialog>
               </Overlay>,
               document.body
            )}
      </Section>
   );
};

export default AntennaIpsSection;

const Section = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;
const SectionTitle = styled.h4`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;
const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
   margin: 0;
`;
const Table = styled.div`
   display: flex;
   flex-direction: column;
   overflow-x: auto;
`;
const grid = `
   display: grid;
   grid-template-columns: minmax(110px, 1.3fr) minmax(110px, 1fr) minmax(110px, 1fr) minmax(80px, 0.8fr) 72px;
   gap: 8px;
   align-items: center;
   min-width: 520px;
   padding: 6px 0;
   font-size: 12px;
`;
const HeadRow = styled.div`
   ${grid}
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Row = styled.div`
   ${grid}
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Mono = styled.span`
   font-family: ui-monospace, Menlo, monospace;
`;
const btn = `
   padding: 6px 12px;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const AddBtn = styled.button`
   ${btn}
   border: 1px dashed ${colors.PRIMARY};
   background: transparent;
   color: ${colors.PRIMARY};
`;
const RemoveBtn = styled.button`
   ${btn}
   padding: 4px 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: ${colors.DANGER};
`;
const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.45);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 3000;
   padding: 16px;
`;
const Dialog = styled.div`
   width: 100%;
   max-width: 420px;
   background: ${colors.WHITE};
   border-radius: 12px;
   padding: 20px;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;
const DialogTitle = styled.h3`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
`;
const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;
const Label = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;
const Input = styled.input`
   padding: 9px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 14px;
   background: transparent;
   color: inherit;
`;
const Select = styled.select`
   padding: 9px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 14px;
   background: transparent;
   color: inherit;
`;
const ErrorText = styled.p`
   margin: 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
const GhostBtn = styled.button`
   ${btn}
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
`;
const PrimaryBtn = styled.button`
   ${btn}
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
`;
const DangerBtn = styled.button`
   ${btn}
   border: none;
   background: ${colors.DANGER};
   color: #fff;
`;
